import type { Key } from 'react'

import type { Optional } from '../types/types'
import { isEmpty, notEmpty } from './typeUtils'

/**
 * Given a list of objects of the same type, will merge the objects into a single object where each value contains an array
 * with all of the source object values.
 */
export const mergeObjects = <ValueType, Keys extends string>(
  ...sources: Partial<Record<Keys, ValueType>>[]
): Record<Keys, ValueType[]> => {
  type PartialReturn = Partial<Record<Keys, ValueType[]>>

  return sources.reduce((acc: PartialReturn, sourceElement) => {
    for (const sourceKey in sourceElement) {
      if (sourceKey in acc) {
        acc[sourceKey]!.push(sourceElement[sourceKey]!)
      } else {
        // const r = Object.defineProperty(acc, sourceKey, { value: [sourceElement[sourceKey]], writable: true })
        acc[sourceKey] = [sourceElement[sourceKey]!]
      }
    }

    return acc
  }, {} as PartialReturn) as Record<Keys, ValueType[]>
}

const appendSearchParams = ({
  key,
  value,
  filter,
  params,
}: {
  key: string
  value: unknown
  filter: (value: unknown) => boolean
  params: URLSearchParams
}) => {
  if (value instanceof Array) {
    // Array is a subtype of Object so it must be checked first.
    value.forEach((item) =>
      appendSearchParams({ key, value: item, filter, params }),
    )
  } else if (value !== null && typeof value === 'object') {
    // typeof is used rather than instanceof to cover more cases.
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      appendSearchParams({ key: nestedKey, value: nestedValue, filter, params })
    })
  } else if (filter(value)) {
    params.append(key, `${value}`)
  }
}

/**
 * Recursively build `URLSearchParams`, handling both array values and nested objects.
 *
 * The value conversion to URL search parameters works as follows:
 * - Array values will cause the its key to be appended one time per item (i.e. `{foo: [1, 2]}` becomes `foo=1&foo=2`).
 *   - Nested arrays will be flattened (i.e. `{foo: [1, [2, 3]]}` becomes `foo=1&foo=2&foo=3`).
 * - Nested objects are flattened, so the key in the outer object is ignored (i.e. `{foo: {bar: 1}, baz: {qux: 2}}` becomes `bar=1&qux=2`).
 * - Anything else is coerced to a string (including booleans and numbers).
 *
 * The default filter will ignore both `undefined` and `null` values, but can be changed if needed.
 * Note that the filter will not receive arrays or objects, only values that will end in the `URLSearchParams`.
 */
export const searchParams = (
  params: Record<string, unknown>,
  filter: (value: unknown) => boolean = notEmpty,
): URLSearchParams => {
  const result = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    appendSearchParams({ key, value, filter, params: result })
  })

  return result
}

/**
 * Try parsing a JSON string, but return undefined instead of throwing an error if it cannot be parsed.
 */
export const tryParseJson = (json: Optional<string>) => {
  try {
    return JSON.parse(json ?? '') as unknown
  } catch {
    return undefined
  }
}

/**
 * Recursively filter all field values out of the object according to the callback function.
 *
 * For example, this can be used to keep all amounts except those that equal to zero with `(x) => x !== 0`.
 */
export const filterFieldsRecursive = <T>(
  object: T,
  callbackFn: (field: unknown, fieldKey: string | number | symbol) => boolean,
): Partial<T> => {
  if (object && typeof object === 'object') {
    return Array.isArray(object)
      ? (object
          .map((value) => filterFieldsRecursive(value, callbackFn))
          .filter(callbackFn) as T) // Typescript >5.9 complains here otherwise. We couldn't fint a better way to type this.
      : Object.fromEntries(
          Object.entries(object)
            .map(([key, value]) => [
              key,
              filterFieldsRecursive(value, callbackFn),
            ])
            .filter(([key, value]) =>
              callbackFn(value, key as string | number | symbol),
            ),
        )
  }

  return object
}

type DiffElement<T> =
  | undefined
  | {
      removed?: T
      added?: T
    }

export type Diff<T> = T extends (infer E)[]
  ? Diff<E>[]
  : T extends Record<string, unknown>
    ?
        | undefined
        | {
            [K in keyof T]: Diff<T[K]>
          }
    : DiffElement<T>

const diffElement = <T>(before: T, after: T): DiffElement<T> => {
  if (isEmpty(before) && notEmpty(after)) {
    return { added: after }
  }

  if (notEmpty(before) && isEmpty(after)) {
    return { removed: before }
  }

  if (typeof before !== typeof after) {
    return { removed: before, added: after }
  }

  if (typeof after !== 'object') {
    // Non-objects can be compared directly, assuming functions don't exist
    return before !== after ? { removed: before, added: after } : undefined
  }

  return diffObjects(
    // Assuming before and after are not arrays,
    // since this is an private function and that shouldn't happen.
    before as Record<string, unknown>,
    after as Record<string, unknown>,
  )
}

export const isDiffElement = (
  element: unknown,
): element is NonNullable<DiffElement<unknown>> =>
  typeof element === 'object' &&
  element !== null &&
  ('removed' in element || 'added' in element)

/**
 * Calculate the difference between two objects.
 *
 * Returns two objects with the same structure as the input,
 * but where non-nested objects are replaced with removed/added pairs.
 *
 * A field is considered changed if both removed and added are set.
 */
export const diffObjects = <T extends Record<string, unknown>>(
  before: T,
  after: T,
): Diff<T> => {
  const result: Record<string, unknown> = {}

  Object.entries(before).forEach(([key, beforeValue]) => {
    if (!(key in after)) {
      result[key] = { removed: beforeValue }
    }
  })

  Object.entries(after).forEach(([key, afterValue]) => {
    if (!(key in before)) {
      result[key] = { added: afterValue }

      return
    }

    const beforeValue = before[key]

    if (!Array.isArray(afterValue)) {
      const diffElem = diffElement(beforeValue, afterValue)

      if (diffElem) {
        result[key] = diffElem
      }
    } else {
      // For now this is handled pair-wise.
      // This means early insertions or removals are not handled very well,
      // because every element after that will appear as "changed" even though
      // it's just been moved.
      if (!Array.isArray(beforeValue)) {
        throw new Error(
          `Expected previous ${key} to be array, not ${typeof beforeValue}`,
        )
      }

      const maxLength = Math.max(beforeValue.length, afterValue.length)
      const diffArray = []

      for (let i = 0; i < maxLength; i += 1) {
        diffArray.push(diffElement(beforeValue[i], afterValue[i]))
      }

      if (diffArray.some((diffElem) => diffElem !== undefined)) {
        result[key] = diffArray
      }
    }
  })

  return (Object.keys(result).length ? result : undefined) as Diff<T>
}

const hasKeys = (value: object) => Object.keys(value).length > 0
const isEmptyObject = (value: unknown) =>
  // JS treats null as an object, we want to check if it's an actual empty object
  value !== null && typeof value === 'object' && !hasKeys(value)

/**
 * Calculate the difference between two objects.
 * The same as diffObjects but returns only the added changes
 *
 * Returns an object with the same structure as the input with the added changes
 */
export const diffObjectsAdded = <T extends Record<string, unknown>>(
  before: T,
  after: T,
): Partial<T> => {
  if (before === after) {
    return {}
  }

  if (typeof before !== 'object' || typeof after !== 'object') {
    return after
  }

  return Object.keys(after).reduce<Record<string, unknown>>((result, key) => {
    if (!(key in before)) {
      result[key] = after[key]
      return result
    }

    const beforeValue = before[key]
    const afterValue = after[key]

    if (Array.isArray(afterValue)) {
      if (!Array.isArray(beforeValue)) {
        result[key] = afterValue
        return result
      }

      const maxLength = Math.max(beforeValue.length, afterValue.length)
      const diff = []

      for (let i = 0; i < maxLength; i += 1) {
        diff.push(diffObjectsAdded(beforeValue[i], afterValue[i]))
      }

      result[key] = diff
        .filter((value) => value !== undefined)
        .filter((value) => (typeof value === 'object' ? hasKeys(value) : true))
      return result
    }

    const diff = diffObjectsAdded(
      beforeValue as Record<string, unknown>,
      afterValue as Record<string, unknown>,
    )

    if (!isEmptyObject(diff)) {
      result[key] = diff
    }

    return result
  }, {}) as Partial<T>
}

/**
 * Recursively compare two objects for equality,
 * returning `true` if all properties in `a` are equal to those in `b`.
 *
 * Note that `b` may have additional properties not present in `a`.
 * These can have any value, and *won't* make the function return `false`.
 * In a sense, `a` dictates what keys will actually be compared.
 *
 * Arrays are compared element-wise, and inequal lengths *will* cause them
 * to be different.
 */
export const deepPartialEquals = (a: unknown, b: unknown): boolean => {
  if (typeof a !== typeof b) {
    return false
  }

  if (typeof a === 'object' && a !== null) {
    if (typeof b !== 'object') {
      throw new Error('Unreachable')
    }

    if (b === null) {
      return false
    }

    if (Array.isArray(a)) {
      // Arrays get special treatment
      if (!Array.isArray(b)) {
        return false
      }

      return (
        a.length === b.length &&
        a.every((item, index) => deepPartialEquals(item, b[index]))
      )
    } else if (Array.isArray(b)) {
      return false
    }

    for (const [key, aValue] of Object.entries(a)) {
      // Only keys from `a` are checked; extra keys in `b` are ignored
      const bValue = (b as Record<string, unknown>)[key]

      if (!deepPartialEquals(aValue, bValue)) {
        return false
      }
    }

    return true
  }

  // Primitives can be compared directly
  return a === b
}

export const objectToSortedString = (obj: unknown): string => {
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      return `array:[${obj.map((item) => objectToSortedString(item)).join(';')}]`
    } else {
      return `object:{${Object.entries(obj)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, value]) => `${key}:${objectToSortedString(value)}`)
        .join(';')}}`
    }
  } else {
    // Convert the value to string and escape any special characters
    return `${typeof obj}:"${String(obj).replace(/"/g, '\\"')}"`
  }
}

/**
 * Returns a new getter that will return the same unique React key for each unique object passed to it.
 *
 * This is only useful if the object identity is stable across renders.
 * For example, it can be used when a cached network response is used to populate the rows of a table.
 *
 * The benefit of using this over bare indices is that the table rows can be filtered or sorted differently,
 * all while their associated key would remain stable.
 *
 * This pattern is described in more detail in
 * https://bluedesk.blogspot.com/2022/04/generating-react-object-keys-with.html.
 */
export const createReactKeyGetterFromObjectIdentity = <T extends WeakKey>() => {
  const keys = new WeakMap<WeakKey, number>()
  let lastId = 0
  return (object: T): Key => {
    let got = keys.get(object)
    if (!got) {
      got = ++lastId
      keys.set(object, got)
    }
    return got
  }
}

export const pickFields = <T extends Record<string, unknown>>(
  object: T,
  fieldsToPick: Record<keyof T, boolean>,
): Partial<T> => {
  if (object && typeof object === 'object') {
    return Object.fromEntries(
      Object.keys(fieldsToPick) // Going through fieldsToPick to keep the order
        .filter((key) => fieldsToPick[key])
        .map((key) => [key, object[key]]),
    ) as Partial<T>
  }
  return {}
}
